import { useContext } from 'react';
import styled from 'styled-components';
import { Button } from 'reactstrap';
import { Formik } from 'formik';
import { useHotkeys } from 'react-hotkeys-hook';

import {
  DeleteButton,
  NameInput,
  SubmitButton,
  TemplateForm,
} from './template-components';
import { getFirstMutationResult } from '../../utils/graphql';
import ConfirmationModal from '../basics/ConfirmationModal';
import { useHideIntercom } from '../../utils/hooks';
import { SHORTCUTS } from '../../constants/shortcuts';
import { SidebarEditContext } from '../../contexts/SidebarEditContext';
import DropdownButton from '../basics/DropdownButton';

const SidebarContainer = styled.div`
  position: sticky;
  z-index: 1;
  height: 100%;
  right: 0;
  overflow: hidden;
  flex-shrink: 0;
  width: 40vw;
  box-shadow: -3px 1px 3px rgba(0, 0, 0, 0.1);
`;

const SidebarTemplateForm = styled(TemplateForm)`
  height: calc(100% - 70px); // Adjust for height of button bar
  border-left: 1px solid var(--border-color);
  background-color: var(--background-color);
  overflow-x: hidden;
  overflow-y: scroll;
`;

const ButtonBar = styled.div`
  width: 100%;
  background-color: var(--background-color);
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 16px 20px 24px 16px;
  box-shadow: 0px -1px 2px rgba(0, 0, 0, 0.25);
  z-index: 1;
`;

// Conditionally style inputs
const SidebarFormContainer = styled.div`
  input:not(${NameInput}),
  button:not(.btn-link),
  .rbt-input-multi,
  textarea {
    border-color: var(--text-muted-color);
    font-size: 0.875rem;
  }

  ${DropdownButton} {
    background-color: var(--background-color);
  }
`;

const SidebarForm = ({
  editing,
  onSubmit,
  onDelete,
  onClose,
  onCancel,
  loading,
  validationSchema,
  showDelete,
  deleteButtonText,
  confirmEditText,
  validationErrorText,
  children,
  status,
  ...props
}) => {
  const { hasErrors } = status;
  const {
    isConfirmEditModalOpen,
    isValidationErrorModalOpen,
    closeModalAndProceed,
    closeModalAndCancel,
  } = useContext(SidebarEditContext);

  const closeOnSuccess = (response) => {
    if (getFirstMutationResult(response?.data)) onClose();
  };

  const submitAndClose = (...params) => {
    onSubmit(...params).then(closeOnSuccess);
  };

  const submitAndProceed = (...params) => {
    onSubmit(...params).then((response) => {
      if (getFirstMutationResult(response?.data)) {
        closeModalAndProceed();
      } else {
        closeModalAndCancel();
      }
    });
  };

  const deleteAndClose = (...params) => {
    onDelete(...params).then(closeOnSuccess);
  };

  useHideIntercom();

  useHotkeys(SHORTCUTS.CLOSE.key, onClose, [onClose]);

  return (
    <SidebarContainer {...props}>
      <Formik
        enableReinitialize
        initialValues={editing}
        onSubmit={submitAndClose}
        validationSchema={validationSchema}
      >
        {(formik) => {
          const { isSubmitting } = formik;
          return (
            <SidebarTemplateForm $removePadding>
              <ConfirmationModal
                title="Save changes?"
                actionDescriptionSentence={confirmEditText}
                actionQuestion="Would you like to save your changes before closing?"
                actionName="Save"
                onConfirm={() => submitAndProceed(formik.values, formik)}
                cancelName="Discard changes"
                onCancel={closeModalAndProceed}
                closeName="Keep editing"
                onClose={closeModalAndCancel}
                isOpen={isConfirmEditModalOpen}
                actionDisabled={hasErrors}
              />
              <ConfirmationModal
                title="Fix errors?"
                actionDescriptionSentence={validationErrorText}
                actionQuestion="Would you like to continue editing?"
                actionName="Keep editing"
                onConfirm={closeModalAndCancel}
                cancelName="Discard changes"
                onCancel={closeModalAndProceed}
                isOpen={isValidationErrorModalOpen}
              />
              <div className="m-3 mr-0">
                <SidebarFormContainer>
                  {typeof children === 'function' ? children(formik) : children}
                </SidebarFormContainer>
              </div>
              <ButtonBar>
                <div className="d-flex justify-content-between">
                  <div>
                    {showDelete && (
                      <DeleteButton
                        onDelete={() => deleteAndClose(editing)}
                        disabled={isSubmitting || loading}
                        size="sm"
                        key={`delete-${editing?.id}`}
                        buttonText={deleteButtonText}
                      />
                    )}
                  </div>
                  <div>
                    <Button
                      // use onCancel if provided, otherwise use onClose
                      onClick={onCancel || onClose}
                      className="ml-1"
                      size="sm"
                      disabled={isSubmitting || loading}
                    >
                      Cancel
                    </Button>
                    <SubmitButton
                      loading={loading}
                      size="sm"
                      className="ml-2"
                    />
                  </div>
                </div>
              </ButtonBar>
            </SidebarTemplateForm>
          );
        }}
      </Formik>
    </SidebarContainer>
  );
};

export default SidebarForm;
